<template>
    <div class="main">
        <div v-show="errorMessage" class="kgr-color kgr-color--error">{{ errorMessage }}</div>
        <div
            class="dropzone-container"
            @dragover="dragover"
            @dragleave="dragleave"
            @drop="drop"
            :style="isDragging && 'border-color: green;'"
        >
            <input
                type="file"
                :multiple="isMultiple"
                :id="id"
                class="hidden-input"
                @change="onChange"
                ref="file"
                :accept="concatAcceptedMimeTypes"
            />
            <label v-show="showHint" :for="id" class="file-label">
                <kgr-icon
                    icon-class="arrow-upward"
                    circle
                    inverted
                    big
                />
                <div v-if="isDragging">{{ tReleaseHere }}</div>
                <div v-else v-html="tDropHere"></div>
            </label>
            <div class="preview-container mt-4" v-if="files.length">
                <div v-for="file in files" :key="file.name" class="preview-card">
                    <div>
                        <img class="preview-img" :src=generateURL(file) />
                        <p>
                            {{ file.name }}
                            {{ showSize ? fileSize(file) : '' }}
                        </p>
                    </div>
                    <div>
                        <button
                            class="ml-2"
                            type="button"
                            @click="remove(files.indexOf(file), true)"
                        >
                            <b class="pointer">x</b>
                            <!-- Could replace with Icon component if desired:
                            <kgr-icon icon-class="close" small />
                            -->
                        </button>
                    </div>
                </div>
            </div>
            <div
                v-show="message !== null"
                class="kgr-color kgr-color--error"
            >
                {{ message }}
            </div>
        </div>
    </div>
</template>

<script>
const MIME_TYPE_JPG = 'image/jpeg';
const MIME_TYPE_PNG = 'image/png';
const MIME_TYPE_GIF = 'image/gif';
const MIME_TYPE_BMP = 'image/bmp';
const MIME_TYPE_PDF = 'application/pdf';

import Icon from '../../icon/Icon.vue';

export default {
    name: 'DropFile',
    components: {
        'kgr-icon': Icon,
    },
    props: {
        customId: {
            type: String,
            required: false,
            default: null,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        multipleExtendedFiles: {
            type: Boolean,
            required: false,
            default: false,
        },
        showSize: {
            type: Boolean,
            required: false,
            default: false,
        },
        jpgOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        jpgPngOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        jpgPngGifOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        jpgPngGifBmpOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        pdfOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        emitFiles: {
            type: Boolean,
            required: false,
            default: false,
        },
        emitBase64Files: {
            type: Boolean,
            required: false,
            default: false,
        },
        errorMessage: {
            type: String,
            required: false,
            default: null,
        },
    },
    computed: {
        id() {
            return this.customId ?? 'dropFileInput';
        },
        showHint() {
            return this.files.length === 0;
        },
        tDropHere() {
            return this.isMultiple ? this.$t('dropFile.multiple.info') : this.$t('dropFile.single.info');
        },
        tReleaseHere() {
            return this.$t('Hier loslassen');
        },
        isMultiple() {
            return this.multiple || this.multipleExtendedFiles;
        },
        noMimeTypeCheck() {
            return !this.jpgPngGifBmpOnly
                && !this.jpgPngGifOnly
                && !this.jpgPngOnly
                && !this.jpgPngOnly
                && !this.pdfOnly;
        },
        concatAcceptedMimeTypes() {
            return this.acceptedMimeTypes.join(',');
        },
        acceptedMimeTypes() {
            if (this.jpgPngGifBmpOnly) {
                return [MIME_TYPE_JPG, MIME_TYPE_PNG, MIME_TYPE_GIF, MIME_TYPE_BMP];
            }
            if (this.jpgPngGifOnly) {
                return [MIME_TYPE_JPG, MIME_TYPE_PNG, MIME_TYPE_GIF];
            }
            if (this.jpgPngOnly) {
                return [MIME_TYPE_JPG, MIME_TYPE_PNG];
            }
            if (this.jpgOnly) {
                return [MIME_TYPE_JPG];
            }
            if (this.pdfOnly) {
                return [MIME_TYPE_PDF];
            }
            return [];
        },
    },
    data() {
        return {
            isDragging: false,
            files: [],
            message: null,
        };
    },
    methods: {
        onChange() {
            let incomingFiles = Array.from(this.$refs.file.files);
            if (!this.isMultiple && incomingFiles.length > 1) {
                this.message = this.$t('Es ist nur eine Datei erlaubt');
                return;
            }
            const fileExist = this.files.length === 0 ? false : this.files.some((r) =>
                incomingFiles.some(file => file.name === r.name && file.size === r.size)
            );
            if (fileExist) {
                this.message = this.$t('Diese Datei existiert schon');
            } else if (!this.isMultiple && this.files.length === 1) {
                this.message = this.$t('Es ist nur eine Datei erlaubt');
            } else {
                this.message = null;
                this.files.push(...incomingFiles);
            }
            this.files = this.files.filter(file => {
                if (!this.isValidMimeType(file.type)) {
                    this.message = this.$t('Ungültiges Dateiformat');
                    return false;
                }
                return true;
            })
            if (!this.files.length) {
                return;
            }
            this.emitChange();
        },
        emitChange() {
            if(this.emitBase64Files) {
                let readers = [];
                this.files.forEach((file, index) => {
                    readers.push(new Promise((resolve, reject) => {
                        let reader = new FileReader();
                        reader.onload = () => {
                            // extract base64 part (without "data:image/png;base64," prefix)
                            const base64String = reader.result.split(',')[1];
                            this.files[index].base64 = base64String;
                            resolve(base64String);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    }));
                });
                Promise.all(readers).then(() => {
                    this.$emit('files', this.files);
                });
            }
            else if (this.emitFiles) {
                this.$emit('files', this.files);
            } else {
                let readers = [];
                this.files.forEach((file, index) => {
                    if (this.multipleExtendedFiles) {
                        readers.push(this.readExtendedFile(index));
                    } else {
                        readers.push(this.readFile(file));
                    }
                });
                Promise.all(readers).then(values => {
                    this.$emit('change', this.isMultiple ? values : values[0]);
                });
            }
        },
        readFile(file) {
            return new Promise(function(resolve, reject){
                let fileReader = new FileReader();
                fileReader.onload = function(){
                    resolve(fileReader.result);
                };
                fileReader.onerror = function(){
                    reject(fileReader);
                };
                fileReader.readAsArrayBuffer(file);
            });
        },
        readExtendedFile(index) {
            return new Promise((resolve, reject) => {
                let reader = new FileReader();
                reader.onload = e => {
                    this.files[index].data = e.target.result;
                    resolve(this.files[index]);
                };
                reader.onerror = reject;
                reader.readAsArrayBuffer(this.files[index]);
            });
        },
        dragover(e) {
            e.preventDefault();
            this.isDragging = true;
        },
        dragleave() {
            this.isDragging = false;
        },
        drop(e) {
            e.preventDefault();
            this.$refs.file.files = e.dataTransfer.files;
            this.onChange();
            this.isDragging = false;
        },
        remove(index, emit = false) {
            this.files.splice(index, 1);
            if (emit === true) {
                this.emitChange();
            }
            this.message = null;
            this.$emit('removeFile');
        },
        generateURL(file) {
            let fileSrc = URL.createObjectURL(file);
            setTimeout(() => {
                URL.revokeObjectURL(fileSrc);
            }, 1000);
            return fileSrc;
        },
        fileSize(file) {
            return (file.size / (1024 * 1024)).toFixed(2) + ' MB';
        },
        resetFiles() {
            this.files = [];
            this.message = null;
        },
        isValidMimeType(mimeType) {
            if (this.noMimeTypeCheck) {
                return true;
            }
            return this.acceptedMimeTypes.includes(mimeType)
        },
    },
}
</script>

<style scoped lang="scss">
.main {
    align-items: center;
    text-align: center;
    position: relative;
}
.dropzone-container {
    min-height: 110px;
    width: 100%;
    padding: 1rem;
    background: #F1F1F1;
    border: 1px dashed #090644;
    border-radius: 16px;
    cursor: pointer;
}
.hidden-input {
    opacity: 0;
    overflow: hidden;
    position: absolute;
    width: 1px;
    height: 1px;
}
.file-label {
    margin: auto;
    font-size: 14px;
    display: block;
    text-align: center;
    cursor: pointer;
}
.pointer {
    cursor: pointer;
}
.preview-container {
    display: flex;
    flex-wrap: wrap;
}
.preview-card {
    display: flex;
    border: 1px solid #a2a2a2;
    padding: 5px;
    margin-left: 5px;
    font-size: 12px;
}
.preview-img {
    width: 45px;
    height: 45px;
    border-radius: 5px;
    border: 1px solid #a2a2a2;
    background-color: #a2a2a2;
}
</style>
